import { useAxiosCP, useAxiosTEQ } from "@/services/axios";
import { countryCodeToCapital } from "@/services/enums/commonEnum";
import { HTTP_USER } from "@/services/enums/httpEnum";
import {
  EditInviteUserResponse,
  InternalUserResponse,
  InviteUserPayload,
  OrganizationResponse,
  UserAccessResponse,
} from "@/stores/user/types";
import axios, { AxiosResponse } from "axios";

/**
 *  Get user organization for organization select ui.
 * @returns {Promise}
 * @param user_id
 * @param payload
 */
const getOrganizations = (
  user_id
): Promise<AxiosResponse<OrganizationResponse>> => {
  return useAxiosCP.get(`${HTTP_USER.GET_ORGANIZATIONS}`, {
    headers: { "X-Api-Key": import.meta.env.VITE_APP_API_KEY_CP },
    params: {
      user_id,
    },
  });
};

/**
 * Get user internal user list.
 * @returns {Promise}
 * @param payload
 */
const getInternalUser = (
  payload
): Promise<AxiosResponse<InternalUserResponse>> => {
  return useAxiosCP.post(`${HTTP_USER.GET_INTERNAL_USER_LIST}`, payload);
};
/**
 * Get user organization list.
 * @returns {Promise}
 * @param payload
 */
const getUserAccess = (payload): Promise<AxiosResponse<UserAccessResponse>> => {
  return useAxiosCP.post(`${HTTP_USER.GET_USER_ACCESS}`, payload);
};

/**
 * send user invitation
 * @param payload
 */

const sendUserInvitation = (payload) => {
  return useAxiosCP.post(HTTP_USER.SENT_USER_INVITATION, payload);
};

/**
 * revoke user invitation
 * @param payload
 */

const revokeUserInvitation = (payload): Promise<AxiosResponse<any>> => {
  return useAxiosCP.post(HTTP_USER.REVOKE_USER_INVITATION, payload);
};

const getUserForEdit = (
  payload: InviteUserPayload
): Promise<AxiosResponse<EditInviteUserResponse>> => {
  return useAxiosCP.post(HTTP_USER.GET_USER_FOR_EDIT, payload);
};

const editUserAccess = (payload: InviteUserPayload) => {
  return useAxiosCP.post(HTTP_USER.EDIT_USER_ACCESS, payload);
};

const deleteUserAccess = (payload: InviteUserPayload) => {
  return useAxiosCP.post(HTTP_USER.DELETE_USER_ACCESS, payload);
};

/**
 * Get company info.
 * @returns {Promise}
 * @param bus_company
 */
const fetchCompanyInfo = (bus_company: string): Promise<AxiosResponse<any>> => {
  return useAxiosTEQ.get(HTTP_USER.COMPANY_INFO, {
    headers: {
      "X-Api-Key": import.meta.env.VITE_APP_API_KEY_TEQ,
      showToast: true,
    },
    params: {
      bus_company,
    },
  });
};

/**
 * Fetches the coordinates of a country's capital city using its country code or name.
 * @param {string} countryIdentifier - The ISO 3166-1 alpha-2/alpha-3 country code or country name.
 * @returns {Promise<{ capital: string; lat: number; lng: number } | null>} - An object containing the capital city name and its coordinates, or null if not found.
 */
const fetchCountryCapitalCoordinates = async (countryIdentifier: string) => {
  try {
    const identifier = countryIdentifier.toUpperCase();
    const apiKey = import.meta.env.VITE_APP_FIREBASE_API_KEY; // Replace with your actual Google API key

    const capitalCity =
      countryCodeToCapital[identifier] ?? countryCodeToCapital["NO"];
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json`,
      {
        params: {
          address: `${capitalCity}, ${identifier}`,
          key: apiKey,
        },
      }
    );
    const result = response.data.results[0];
    if (result) {
      const lat = result.geometry.location.lat;
      const lng = result.geometry.location.lng;
      const formattedAddress = result.formatted_address;
      return { capital: formattedAddress, coordinates: { lat, lng } };
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export {
  getOrganizations,
  getUserAccess,
  sendUserInvitation,
  revokeUserInvitation,
  getInternalUser,
  getUserForEdit,
  editUserAccess,
  deleteUserAccess,
  fetchCompanyInfo,
  fetchCountryCapitalCoordinates,
};
